import { UniversityApplicationsBars } from '../../components/dashboard/UniversityApplicationsBars';
import { applicationStatusBreakdown } from '../../utils/applicationStatusBreakdown';
import { DashboardWelcome } from '../../components/dashboard/DashboardWelcome';
import { AssignedContactPanel } from '../../components/dashboard/AssignedContactPanel';
import { ApplicationsOverview } from '../../components/dashboard/ApplicationsOverview';
import { ApplicationStatusLegend } from '../../components/applications/ApplicationStatusLegend';
import { normalizeApplicationStatus, applicationStatusLabel } from '../../../../shared/src/utils/applicationStatus';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  FileText, 
  CheckCircle,
  AlertCircle,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import { 
  PieChart as RechartsPieChart, 
  Pie,
  Cell, 
  Tooltip, 
  ResponsiveContainer
} from 'recharts';
import { AgentStats } from '@/types/user';
import { agentApi } from '@/services/agentApi';

const StatCard: React.FC<{
  to?: string;
  unavailable?: boolean;
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  trend?: 'up' | 'down' | 'stable';
}> = ({ to, unavailable, title, value, icon, color, change, changeType = 'neutral', trend }) => {
  const changeColor = {
    positive: 'text-success',
    negative: 'text-danger',
    neutral: 'text-muted'
  }[changeType];

  const trendIcon = {
    up: <ArrowUpRight size={16} className="text-success" />,
    down: <ArrowDownRight size={16} className="text-danger" />,
    stable: <Activity size={16} className="text-muted" />
  }[trend || 'stable'];

  const content = (
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className={`p-3 rounded-3`} style={{ backgroundColor: `${color}15`, color: color }}>
              {icon}
            </div>
            {trend && (
              <div className="d-flex align-items-center">
                {trendIcon}
              </div>
            )}
          </div>
          <h6 className="card-title text-muted mb-1">{title}</h6>
          <h3 className="fw-bold mb-2">{unavailable ? 'Unavailable' : value}</h3>
          {change && !unavailable && (
            <div className="d-flex align-items-center">
              <small className={changeColor}>
                {change}
              </small>
            </div>
          )}
        </div>
  );
  const cardClass = 'card h-100 border-0 shadow-sm hover-lift application-stat-card';
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="col-md-6 col-lg-4 mb-4"
    >
      {to && !unavailable ? (
        <Link to={to} className={`${cardClass} text-decoration-none text-body`} aria-label={`View ${title.toLowerCase()}`}>
          {content}
        </Link>
      ) : (
        <div className={cardClass}>{content}</div>
      )}
    </motion.div>
  );
};

// Application Status Chart
export const ApplicationStatusChart: React.FC<{ stats: AgentStats }> = ({ stats }) => {
  const data = applicationStatusBreakdown(stats);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="col-lg-4 mb-4"
    >
      <div className="card h-100 border-0 shadow-sm">
        <div className="card-header bg-white border-0 pb-0">
          <h5 className="card-title mb-0">Application Status</h5>
        </div>
        <div className="card-body">
          <ResponsiveContainer width="100%" height={300}>
            <RechartsPieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip 
                formatter={(value: number, name: string, props: any) => [
                  `${value} (${props.payload.percentage}%)`,
                  name
                ]}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
          <ApplicationStatusLegend entries={data} />
        </div>
      </div>
    </motion.div>
  );
};

// Top Universities Chart
const TopUniversitiesChart: React.FC = () => {
  const [chartData, setChartData] = useState<Array<{ name: string; applications: number; success: number }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTopUniversities = async () => {
      try {
        setLoading(true);
        
        const result = await agentApi.getApplications({
          limit: 1000,
        });

        if (!result?.applications) {
          setChartData([]);
          return;
        }

        const universityMap = new Map<string, { name: string; applications: number; success: number }>();

        result.applications.forEach((app: any) => {
          const universityName = app.universities?.name || 'Unknown University';
          
          if (!universityMap.has(universityName)) {
            universityMap.set(universityName, {
              name: universityName,
              applications: 0,
              success: 0,
            });
          }

          const university = universityMap.get(universityName)!;
          university.applications += 1;

          if (
            app.status === 'APPROVED' ||
            app.status === 'PAID' ||
            app.status === 'TUITION_PAYMENT_STAGE' ||
            app.status === 'VISA_APPROVED'
          ) {
            university.success += 1;
          }
        });

        const sortedData = Array.from(universityMap.values())
          .sort((a, b) => b.applications - a.applications)
          .slice(0, 5);

        setChartData(sortedData);
      } catch (error) {
        console.error('Error fetching top universities:', error);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTopUniversities();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="col-lg-6 mb-4"
    >
      <div className="card h-100 border-0 shadow-sm">
        <div className="card-header bg-white border-0 pb-0">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <h5 className="card-title mb-0">Top Universities by Applications</h5>
            <div className="d-flex gap-2">
              <span className="badge bg-primary bg-opacity-10 text-primary">Total</span>
              <span className="badge bg-success bg-opacity-10 text-success">Success</span>
            </div>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: '250px' }}>
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : chartData.length === 0 ? (
            <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: '250px' }}>
              <p className="mb-0">No data available</p>
            </div>
          ) : (
            <UniversityApplicationsBars data={chartData} />
          )}
        </div>
      </div>
    </motion.div>
  );
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;
  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
  const diffInMonths = Math.floor(diffInDays / 30);
  return `${diffInMonths}mo ago`;
};

const RecentActivity: React.FC = () => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Array<{
    id: number;
    title: string;
    description: string;
    time: string;
    icon: React.ReactNode;
    bgClass: string;
    applicationReference?: string;
    applicationId?: number;
  }>>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentActivities = async () => {
      try {
        setLoading(true);
        const result = await agentApi.getApplications({
          page: 1,
          limit: 5,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        });

        const activityPromises = result.applications.map(async (app) => {
          try {
            const activityResult = await agentApi.getApplicationActivity(app.id, { limit: 5 });
            const logs = activityResult.auditLogs || [];
            return logs.map((log: any) => {
              const getActivityConfig = () => {
                if (log.action === 'UPDATE_APPLICATION_STATUS') {
                  const newStatus = log.newValues?.status || 'Unknown';
                  return {
                    icon: <CheckCircle size={20} className="text-primary" />,
                    bgClass: 'bg-primary bg-opacity-10',
                    title: `Status updated to ${applicationStatusLabel(newStatus, true)}`,
                  };
                } else if (log.action === 'ADD_APPLICATION_NOTE' || log.action === 'UPDATE_APPLICATION_NOTES') {
                  return {
                    icon: <FileText size={20} className="text-info" />,
                    bgClass: 'bg-info bg-opacity-10',
                    title: 'Note added',
                  };
                }
                return {
                  icon: <FileText size={20} className="text-secondary" />,
                  bgClass: 'bg-secondary bg-opacity-10',
                  title: log.action.replace(/_/g, ' '),
                };
              };

              const config = getActivityConfig();
              const studentName = `${app.firstName} ${app.lastName}`;
              const universityName = app.universities?.name || 'University';
              let description = `${universityName} - ${studentName}`;
              
              if (log.action === 'UPDATE_APPLICATION_STATUS' && log.oldValues?.status && log.newValues?.status) {
                description = `${universityName} - ${studentName}: ${applicationStatusLabel(log.oldValues.status, true)} → ${applicationStatusLabel(log.newValues.status, true)}`;
              } else if (log.action === 'ADD_APPLICATION_NOTE' && log.newValues?.note) {
                const notePreview = log.newValues.note.length > 50 
                  ? log.newValues.note.substring(0, 50) + '...' 
                  : log.newValues.note;
                description = `${universityName} - ${studentName}: ${notePreview}`;
              }

              return {
                id: log.id || `${app.id}-${Date.now()}`,
                title: config.title,
                description,
                time: formatTimeAgo(log.createdAt),
                icon: config.icon,
                bgClass: config.bgClass,
                applicationReference: app.reference,
                applicationId: app.id,
                timestamp: new Date(log.createdAt).getTime(),
              };
            });
          } catch (error) {
            console.error(`Error fetching activity for application ${app.id}:`, error);
            return [];
          }
        });

        const allActivities = await Promise.all(activityPromises);
        const flattenedActivities = allActivities.flat();
        
        const applicationActivities = result.applications.map((app) => {
          const getActivityConfig = (status: string) => {
            switch (normalizeApplicationStatus(status)) {
              case 'APPROVED':
                return {
                  icon: <CheckCircle size={20} className="text-success" />,
                  bgClass: 'bg-success bg-opacity-10',
                  title: 'Application approved',
                };
              case 'VISA_APPROVED':
                return {
                  icon: <CheckCircle size={20} className="text-success" />,
                  bgClass: 'bg-success bg-opacity-10',
                  title: 'Visa approved',
                };
              case 'REJECTED':
                return {
                  icon: <AlertCircle size={20} className="text-danger" />,
                  bgClass: 'bg-danger bg-opacity-10',
                  title: 'Application rejected',
                };
              default:
                return {
                  icon: <FileText size={20} className="text-primary" />,
                  bgClass: 'bg-primary bg-opacity-10',
                  title: 'Application created',
                };
            }
          };

          const config = getActivityConfig(app.status);
          const studentName = `${app.firstName} ${app.lastName}`;
          const universityName = app.universities?.name || 'University';
          const description = `${universityName} application for ${studentName}`;
          
          const activityDate = (app.status !== 'DRAFT' && app.submittedAt) 
            ? app.submittedAt 
            : app.createdAt;

          return {
            id: `app-${app.id}`,
            title: config.title,
            description,
            time: formatTimeAgo(activityDate),
            icon: config.icon,
            bgClass: config.bgClass,
            applicationId: app.id,
            timestamp: new Date(activityDate).getTime(),
          };
        });

        const combinedActivities = [
          ...flattenedActivities,
          ...applicationActivities,
        ];

        const sortedActivities = combinedActivities
          .sort((a, b) => {
            const aTime = a.timestamp || 0;
            const bTime = b.timestamp || 0;
            return bTime - aTime;
          })
          .slice(0, 5)
          .map(({ timestamp, ...rest }) => rest);

        setActivities(sortedActivities);
      } catch (error) {
        console.error('Error fetching recent activities:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentActivities();
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="col-lg-6 mb-4"
    >
      <div className="card h-100 border-0 shadow-sm">
        <div className="card-header bg-white border-0 pb-0">
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="card-title mb-0">Recent Activity</h5>
            <span className="badge bg-primary bg-opacity-10 text-primary">Live</span>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : activities.length === 0 ? (
            <div className="text-center text-muted py-4">
              <p className="mb-0">No recent activity</p>
            </div>
          ) : (
            activities.map((activity, index) => (
              <div 
                key={activity.id} 
                className={`d-flex align-items-center ${index < activities.length - 1 ? 'mb-3' : ''} ${activity.applicationId ? 'cursor-pointer' : ''}`}
                style={activity.applicationId ? { cursor: 'pointer' } : {}}
                onClick={() => {
                  if (activity.applicationId) {
                    navigate(`/applications/${activity.applicationId}`);
                  }
                }}
                onMouseEnter={(e) => {
                  if (activity.applicationId) {
                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activity.applicationId) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <div className={`${activity.bgClass} rounded-circle p-2 me-3`}>
                  {activity.icon}
                </div>
                <div className="flex-grow-1">
                  <p className="mb-1 fw-semibold">{activity.title}</p>
                  <small className="text-muted">{activity.description}</small>
                </div>
                <small className="text-muted">{activity.time}</small>
              </div>
            ))
          )}
        </div>
      </div>
    </motion.div>
  );
};

export const SubAccountDashboardPage: React.FC = () => {
  const [stats, setStats] = useState<AgentStats>({
    totalApplications: 0,
    pendingApplications: 0,
    draftApplications: 0,
    approvedApplications: 0,
    rejectedApplications: 0,
    tuitionPaymentStageApplications: 0,
    paidApplications: 0,
    totalEarnings: 0,
    pendingEarnings: 0,
    thisMonthEarnings: 0,
    lastMonthEarnings: 0,
    averageCommission: 0,
    thisMonthAverageCommission: 0,
    lastMonthAverageCommission: 0,
    successRate: 0,
    thisMonthSuccessRate: 0,
    lastMonthSuccessRate: 0,
    thisMonthApprovedApplications: 0,
    lastMonthApprovedApplications: 0,
    thisMonthPendingApplications: 0,
    lastMonthPendingApplications: 0,
    thisMonthTotalApplications: 0,
    lastMonthTotalApplications: 0,
    thisMonthPendingEarnings: 0,
    lastMonthPendingEarnings: 0,
  });
  const [statsState, setStatsState] = useState<"loading" | "ready" | "error">("loading");
  const [statsRetry, setStatsRetry] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    let active = true;
    setStatsState("loading");
    const fetchStats = async () => {
      try {
        const data = await agentApi.getStats();
        if (active) { setStats(data); setStatsState("ready"); }
      } catch (error) {
        if (active) setStatsState("error");
      }
    };
    fetchStats();
    return () => { active = false; };
  }, [statsRetry]);

  const handleExportReport = async () => {
    try {
      setIsExporting(true);
      
      const applicationsResult = await agentApi.getApplications({ limit: 1000 });

      const csvRows: string[] = [];
      
      csvRows.push('Dashboard Report - ' + new Date().toLocaleDateString());
      csvRows.push('');
      
      csvRows.push('SUMMARY STATISTICS');
      csvRows.push('Metric,Value');
      csvRows.push(`Total Applications,${stats.totalApplications || 0}`);
      csvRows.push(`Submitted Applications,${stats.pendingApplications || 0}`);
      csvRows.push(`Draft Applications,${stats.draftApplications || 0}`);
      csvRows.push(`Approved Applications,${stats.approvedApplications || 0}`);
      csvRows.push(`Rejected Applications,${stats.rejectedApplications || 0}`);
      csvRows.push(`Success Rate,${(stats.successRate || 0).toFixed(2)}%`);
      csvRows.push('');
      
      csvRows.push('APPLICATIONS');
      csvRows.push('Reference,Student Name,University,Program,Status,Created Date,Submitted Date');
      if (applicationsResult?.applications) {
        applicationsResult.applications.forEach((app: any) => {
          const studentName = `${app.firstName || ''} ${app.lastName || ''}`.trim();
          const universityName = app.universities?.name || 'N/A';
          const programName = app.universityPrograms?.name || 'N/A';
          const createdDate = app.createdAt ? new Date(app.createdAt).toLocaleDateString() : 'N/A';
          const submittedDate = app.submittedAt ? new Date(app.submittedAt).toLocaleDateString() : 'N/A';
          
          csvRows.push(
            `"${app.reference || 'N/A'}","${studentName}","${universityName}","${programName}","${app.status || 'N/A'}","${createdDate}","${submittedDate}"`
          );
        });
      }
      
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `dashboard-report-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="container-fluid py-4">
      <DashboardWelcome onExport={handleExportReport} exporting={isExporting} />

      <div className="row mb-4">
        {statsState === "error" && <div className="col-12 alert alert-danger" role="alert">Dashboard counts unavailable. <button className="btn btn-sm btn-outline-danger" onClick={() => setStatsRetry(value => value + 1)}>Retry</button></div>}
        <StatCard
          title="Total Applications"
          to="/applications"
          unavailable={statsState !== "ready"}
          value={stats.totalApplications || 0}
          icon={<FileText size={24} />}
          color="#000046"
          change={
            stats.thisMonthTotalApplications !== undefined && stats.lastMonthTotalApplications !== undefined && stats.lastMonthTotalApplications > 0
              ? `${((stats.thisMonthTotalApplications / stats.lastMonthTotalApplications - 1) * 100).toFixed(1)}% from last month`
              : stats.thisMonthTotalApplications !== undefined && stats.lastMonthTotalApplications !== undefined
              ? stats.thisMonthTotalApplications > stats.lastMonthTotalApplications
                ? `+${stats.thisMonthTotalApplications - stats.lastMonthTotalApplications} from last month`
                : stats.thisMonthTotalApplications < stats.lastMonthTotalApplications
                ? `${stats.thisMonthTotalApplications - stats.lastMonthTotalApplications} from last month`
                : 'No change from last month'
              : stats.totalApplications && stats.totalApplications > 0
              ? 'Based on all applications'
              : 'No data'
          }
          changeType={
            stats.thisMonthTotalApplications !== undefined && stats.lastMonthTotalApplications !== undefined
              ? stats.thisMonthTotalApplications >= stats.lastMonthTotalApplications ? 'positive' : 'negative'
              : 'neutral'
          }
          trend={
            stats.thisMonthTotalApplications !== undefined && stats.lastMonthTotalApplications !== undefined
              ? stats.thisMonthTotalApplications >= stats.lastMonthTotalApplications ? 'up' : 'down'
              : 'stable'
          }
        />
        <StatCard
          title="Submitted Applications"
          to="/applications?group=pending"
          unavailable={statsState !== "ready"}
          value={stats.pendingApplications || 0}
          icon={<AlertCircle size={24} />}
          color="#d97706"
          change={
            stats.thisMonthPendingApplications !== undefined && stats.lastMonthPendingApplications !== undefined && stats.lastMonthPendingApplications > 0
              ? `${((stats.thisMonthPendingApplications / stats.lastMonthPendingApplications - 1) * 100).toFixed(1)}% from last month`
              : stats.thisMonthPendingApplications !== undefined && stats.lastMonthPendingApplications !== undefined
              ? stats.thisMonthPendingApplications > stats.lastMonthPendingApplications
                ? `+${stats.thisMonthPendingApplications - stats.lastMonthPendingApplications} from last month`
                : stats.thisMonthPendingApplications < stats.lastMonthPendingApplications
                ? `${stats.thisMonthPendingApplications - stats.lastMonthPendingApplications} from last month`
                : 'No change from last month'
              : stats.pendingApplications && stats.pendingApplications > 0
              ? 'Based on all applications'
              : 'No data'
          }
          changeType={
            stats.thisMonthPendingApplications !== undefined && stats.lastMonthPendingApplications !== undefined
              ? stats.thisMonthPendingApplications >= stats.lastMonthPendingApplications ? 'positive' : 'negative'
              : 'neutral'
          }
          trend={
            stats.thisMonthPendingApplications !== undefined && stats.lastMonthPendingApplications !== undefined
              ? stats.thisMonthPendingApplications >= stats.lastMonthPendingApplications ? 'up' : 'down'
              : 'stable'
          }
        />
        <StatCard
          title="Approved Applications"
          to="/applications?group=approved"
          unavailable={statsState !== "ready"}
          value={stats.approvedApplications || 0}
          icon={<CheckCircle size={24} />}
          color="#059669"
          change={
            stats.thisMonthApprovedApplications !== undefined && stats.lastMonthApprovedApplications !== undefined && stats.lastMonthApprovedApplications > 0
              ? `${((stats.thisMonthApprovedApplications / stats.lastMonthApprovedApplications - 1) * 100).toFixed(1)}% from last month`
              : stats.thisMonthApprovedApplications !== undefined && stats.lastMonthApprovedApplications !== undefined
              ? stats.thisMonthApprovedApplications > stats.lastMonthApprovedApplications
                ? `+${stats.thisMonthApprovedApplications - stats.lastMonthApprovedApplications} from last month`
                : stats.thisMonthApprovedApplications < stats.lastMonthApprovedApplications
                ? `${stats.thisMonthApprovedApplications - stats.lastMonthApprovedApplications} from last month`
                : 'No change from last month'
              : stats.approvedApplications && stats.approvedApplications > 0
              ? 'Based on all applications'
              : 'No data'
          }
          changeType={
            stats.thisMonthApprovedApplications !== undefined && stats.lastMonthApprovedApplications !== undefined
              ? stats.thisMonthApprovedApplications >= stats.lastMonthApprovedApplications ? 'positive' : 'negative'
              : 'neutral'
          }
          trend={
            stats.thisMonthApprovedApplications !== undefined && stats.lastMonthApprovedApplications !== undefined
              ? stats.thisMonthApprovedApplications >= stats.lastMonthApprovedApplications ? 'up' : 'down'
              : 'stable'
          }
        />

      </div>

      <div className="row mb-4">
        {statsState === 'ready' ? <ApplicationStatusChart stats={stats} /> : <div className="col-lg-4 mb-4"><div className="card h-100 border-0 shadow-sm p-4" role="status">{statsState === 'loading' ? 'Loading application status…' : 'Application status unavailable.'}</div></div>}
        <div className="col-lg-8 mb-4">
          <ApplicationsOverview />
        </div>
      </div>

      <div className="dashboard-support-layout">
        <div className="dashboard-support-layout__content">
          <div className="row">
            <TopUniversitiesChart />
            <RecentActivity />
          </div>
        </div>
        <div className="dashboard-support-layout__contacts"><AssignedContactPanel /></div>
      </div>
    </div>
  );
};

